import React from 'react';
import { Helmet } from 'react-helmet';
import AdSenseBlock from "../components/AdSenseBlock";
import { AD_SLOTS } from "../constants/adSlots";

const About = () => (
  <div style={{
    padding: '1rem',
    lineHeight: 1.6,
    textAlign: 'left',
    maxWidth: '820px',
    margin: '0 auto'
  }}>

    {/* SEO */}
    <Helmet>
      <title>TrailRunSim とは｜GPX解析と完走戦略を自動生成するトレラン支援ツール</title>
      <meta
        name="description"
        content="TrailRunSim は GPX と走力から現実的なレース戦略を自動生成するトレイルランニング支援ツールです。開発背景、機能、対象ランナー、アプリの価値を詳しく紹介します。"
      />
      <meta
        name="keywords"
        content="TrailRunSim, トレイルランニング, トレラン, GPX解析, ペース計算, レース戦略, 関門対策, 完走計画"
      />

      {/* JSON-LD */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "TrailRunSim",
            "url": "https://trailrunsim.com",
            "applicationCategory": "Sports",
            "operatingSystem": "All",
            "description": "GPX解析と走力から現実的なレース戦略を自動生成するトレイルランニング支援ツール",
            "inLanguage": "ja"
          }
        `}
      </script>
    </Helmet>

    {/* ------------------------------ */}
    {/* 🍏 H2: TrailRunSim とは */}
    {/* ------------------------------ */}
    <h2>TrailRunSim とは</h2>

    <p style={{ margin: '0.6rem 0 1.2rem', color: '#444' }}>
      TrailRunSim は、トレイルランナーが<strong>自分の走力に合わせて現実的なレース戦略を立てられる</strong>ように設計された
      シミュレーションツールです。GPX の読み込み、ペース計算、エイド到着予測、関門判定などを通じて、
      「完走に必要な情報」をわかりやすく可視化します。
    </p>

    {/* ------------------------------ */}
    {/* 🍏 H2: 開発の背景 */}
    {/* ------------------------------ */}
    <h2>開発の背景</h2>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      トレイルランニングを始めた頃は「どれくらいのペースで走れば完走できるのか」がわからず、
      不安なままスタートラインに立つことが多くありました。
    </p>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      累積標高やコース難易度を数字として把握するのは難しく、事前の戦略づくりに苦労していました。
    </p>

    <div style={{
      marginTop: '1rem',
      padding: '0.8rem',
      background: 'rgba(255,255,255,0.6)',
      borderRadius: '10px'
    }}>
      <strong>開発のきっかけ</strong>
      <p style={{ marginTop: '0.4rem', color: '#444' }}>
        「GPX と走力から、現実的なレース戦略を自動で作れたら便利なのでは？」  
        という思いから、このアプリの開発を始めました。
      </p>
    </div>

    {/* ------------------------------ */}
    {/* 🍏 H2: TrailRunSim でできること */}
    {/* ------------------------------ */}
    <h2>TrailRunSim でできること</h2>

    <ul style={{ marginTop: '0.4rem' }}>
      <li>
        <strong>GPX を読み込んでコースを自動解析</strong><br />
        距離・累積標高・上り/下り比率・最大傾斜などを瞬時に可視化。
      </li>

      <li>
        <strong>走力と基準ペースから現実的なペース配分を自動計算</strong><br />
        ITRA PI、ジョグペース、傾斜、累積標高、疲労を統合して最適化。
      </li>

      <li>
        <strong>エイド到着予測と関門判定</strong><br />
        各エイドの到着時刻を自動計算し、関門に間に合うかを判定。
      </li>

      <li>
        <strong>完走に向けた戦略立案をサポート</strong><br />
        ペースの落ちやすい区間や、時間を稼ぐべき区間が明確に。
      </li>
    </ul>

    <AdSenseBlock adSlot={AD_SLOTS.ABOUT} />

    {/* ------------------------------ */}
    {/* 🍏 H2: 対象ランナー */}
    {/* ------------------------------ */}
    <h2>対象ランナー</h2>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      TrailRunSim は、タイムを競うよりも<strong>完走を目指すランナー</strong>を主な対象としています。
      自然の中を楽しみながら、自分のペースでレースを走りたい方に向けて作られています。
    </p>

    <div style={{
      marginTop: '1rem',
      padding: '0.8rem',
      background: 'rgba(255,255,255,0.6)',
      borderRadius: '10px'
    }}>
      <strong>トレランの魅力</strong>
      <p style={{ marginTop: '0.4rem', color: '#444' }}>
        エイドの食事、景色、他のランナーとの交流、装備選びなど、  
        走る時間そのものを楽しむ文化があります。
      </p>
    </div>

    {/* ------------------------------ */}
    {/* 🍏 H2: 開発者の想い */}
    {/* ------------------------------ */}
    <h2>開発者の想い</h2>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      私自身も「ボリュームゾーン」と呼ばれる一般ランナーであり、速さよりも<strong>完走と楽しさ</strong>を大切にしています。
    </p>

    <p style={{ margin: '0.4rem 0 0.8rem', color: '#444' }}>
      このアプリは、同じ価値観を持つランナーが、より安心してレースに挑めるようにという思いで作られています。
    </p>

    {/* ------------------------------ */}
    {/* 🍏 H2: 関連ページ（内部リンク強化） */}
    {/* ------------------------------ */}
    <h2>関連ページ</h2>

    <p style={{ margin: '0.4rem 0 1rem', color: '#444' }}>
      TrailRunSim をより深く活用したい方へ、関連ページをまとめました。
    </p>

    <ul style={{ lineHeight: 1.7 }}>
      <li>
        <a href="/" style={{ color: '#2c7be5', fontWeight: 600 }}>
          トップページ（シミュレーター）
        </a>
      </li>
      <li>
        <a href="/how-to-use" style={{ color: '#2c7be5', fontWeight: 600 }}>
          使い方ガイド｜トレランアプリで完走計画
        </a>
      </li>
      <li>
        <a href="/AffordableGear" style={{ color: '#2c7be5', fontWeight: 600 }}>
          トレランギア特集
        </a>
      </li>
    </ul>

    {/* ------------------------------ */}
    {/* 🍏 注意書き */}
    {/* ------------------------------ */}
    <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#555' }}>
      ※本アプリは非公式の予測ツールです。大会主催者が定めるルールや関門情報が最優先されますので、必ず公式情報をご確認ください。
    </p>

  </div>
);

export default About;
